import { useQuery, useQueryClient } from "@tanstack/react-query";
import { hasSupabase, supabase } from "./supabase";
import { useRealtimeTable } from "./realtime";
import {
  mockAgents,
  mockApprovals,
  mockDispatch,
  mockEmbeds,
  mockHeartbeats,
  mockNotes,
  mockProjectNotes,
  mockProjects,
  mockRuns,
  mockServices,
  mockTasks,
} from "../data/mock";
import type {
  Agent,
  Approval,
  DispatchRow,
  EmbeddedDashboard,
  Heartbeat,
  Note,
  Project,
  ProjectNote,
  ProjectTask,
  Run,
  Service,
} from "../types/database";
import type {
  CalendarEvent,
  CalendarFeedItem,
  Priority,
} from "../types/calendar";

// Each hook wraps a table read. When Supabase is wired, it queries; when not,
// it returns the mock fixtures. Realtime invalidation is bolted on so the SPA
// "feels alive" even in mock mode (mock just resolves once and stays stable).

async function fetchTable<T>(table: string, mock: T[], orderBy?: { col: string; ascending?: boolean }): Promise<T[]> {
  if (!hasSupabase || !supabase) return mock;
  try {
    let q = supabase.from(table).select("*");
    if (orderBy) q = q.order(orderBy.col, { ascending: orderBy.ascending ?? false });
    const { data, error } = await q;
    if (error) {
      // Migrations 0004/0005 may not yet be applied — fall back gracefully.
      const code = (error as { code?: string }).code;
      if (code === "42P01" || code === "PGRST205") {
        console.info(`[meanie] table ${table} not present yet — using mock`);
        return mock;
      }
      console.warn(`[meanie] query ${table} failed`, error);
      return mock;
    }
    return (data ?? []) as T[];
  } catch (err) {
    console.warn(`[meanie] query ${table} threw`, err);
    return mock;
  }
}

export function useProjects() {
  const qc = useQueryClient();
  useRealtimeTable("projects", () => qc.invalidateQueries({ queryKey: ["projects"] }));
  return useQuery({
    queryKey: ["projects"],
    queryFn: () => fetchTable<Project>("projects", mockProjects, { col: "name", ascending: true }),
  });
}

export function useProject(id: string | undefined) {
  const { data: projects, ...rest } = useProjects();
  return {
    ...rest,
    data: projects?.find((p) => p.id === id),
  };
}

export function useAgents() {
  const qc = useQueryClient();
  useRealtimeTable("agents", () => qc.invalidateQueries({ queryKey: ["agents"] }));
  return useQuery({
    queryKey: ["agents"],
    queryFn: () => fetchTable<Agent>("agents", mockAgents),
  });
}

export function useRuns(project?: string) {
  const qc = useQueryClient();
  useRealtimeTable("runs", () => qc.invalidateQueries({ queryKey: ["runs"] }));
  return useQuery({
    queryKey: ["runs", project ?? "all"],
    queryFn: async () => {
      const all = await fetchTable<Run>("runs", mockRuns, { col: "started_at" });
      return project ? all.filter((r) => r.project === project) : all;
    },
  });
}

export function useApprovals(project?: string) {
  const qc = useQueryClient();
  useRealtimeTable("approvals", () => qc.invalidateQueries({ queryKey: ["approvals"] }));
  return useQuery({
    queryKey: ["approvals", project ?? "all"],
    queryFn: async () => {
      const all = await fetchTable<Approval>("approvals", mockApprovals, { col: "requested_at" });
      return project ? all.filter((a) => a.project === project) : all;
    },
  });
}

export function useNotes(project?: string) {
  const qc = useQueryClient();
  useRealtimeTable("notes", () => qc.invalidateQueries({ queryKey: ["notes"] }));
  return useQuery({
    queryKey: ["notes", project ?? "all"],
    queryFn: async () => {
      const all = await fetchTable<Note>("notes", mockNotes, { col: "created_at" });
      return project ? all.filter((n) => n.project === project) : all;
    },
  });
}

export function useHeartbeats() {
  const qc = useQueryClient();
  useRealtimeTable("heartbeats", () => qc.invalidateQueries({ queryKey: ["heartbeats"] }));
  return useQuery({
    queryKey: ["heartbeats"],
    queryFn: () => fetchTable<Heartbeat>("heartbeats", mockHeartbeats, { col: "created_at" }),
  });
}

export function useDispatchQueue() {
  const qc = useQueryClient();
  useRealtimeTable("dispatch_queue", () => qc.invalidateQueries({ queryKey: ["dispatch_queue"] }));
  return useQuery({
    queryKey: ["dispatch_queue"],
    queryFn: () => fetchTable<DispatchRow>("dispatch_queue", mockDispatch, { col: "enqueued_at" }),
  });
}

// V1.5 cockpit hooks — tasks, project notes, services, embedded dashboards.
// All four tables are added in migration 0004; until that lands in production
// the new hooks fall back to the seeded mock fixtures.

export function useTasks(project?: string) {
  const qc = useQueryClient();
  useRealtimeTable("project_tasks", () => qc.invalidateQueries({ queryKey: ["project_tasks"] }));
  return useQuery({
    queryKey: ["project_tasks", project ?? "all"],
    queryFn: async () => {
      const all = await fetchTable<ProjectTask>("project_tasks", mockTasks, { col: "created_at" });
      return project ? all.filter((t) => t.project === project) : all;
    },
  });
}

export function useProjectNotes(project?: string) {
  const qc = useQueryClient();
  useRealtimeTable("project_notes", () => qc.invalidateQueries({ queryKey: ["project_notes"] }));
  return useQuery({
    queryKey: ["project_notes", project ?? "all"],
    queryFn: async () => {
      const all = await fetchTable<ProjectNote>("project_notes", mockProjectNotes, {
        col: "created_at",
      });
      return project ? all.filter((n) => n.project === project) : all;
    },
  });
}

export function useServices() {
  const qc = useQueryClient();
  useRealtimeTable("services", () => qc.invalidateQueries({ queryKey: ["services"] }));
  return useQuery({
    queryKey: ["services"],
    queryFn: () =>
      fetchTable<Service>("services", mockServices, { col: "last_checked_at", ascending: false }),
  });
}

export function useServicesRenewingSoon() {
  return useQuery({
    queryKey: ["services_renewing_soon"],
    queryFn: async () => {
      const sevenDaysFromNow = Date.now() + 7 * 86_400_000;
      if (!hasSupabase || !supabase) {
        return mockServices.filter(
          (s) => s.renews_at && new Date(s.renews_at).getTime() < sevenDaysFromNow,
        );
      }
      try {
        const { data, error } = await supabase.from("services_renewing_soon").select("*");
        if (error) {
          // View not yet created — derive client-side from services.
          const { data: all } = await supabase.from("services").select("*");
          return ((all ?? []) as Service[]).filter(
            (s) => s.renews_at && new Date(s.renews_at).getTime() < sevenDaysFromNow,
          );
        }
        return (data ?? []) as Service[];
      } catch {
        return mockServices.filter(
          (s) => s.renews_at && new Date(s.renews_at).getTime() < sevenDaysFromNow,
        );
      }
    },
  });
}

export function useEmbeddedDashboards(project?: string) {
  const qc = useQueryClient();
  useRealtimeTable("embedded_dashboards", () =>
    qc.invalidateQueries({ queryKey: ["embedded_dashboards"] }),
  );
  return useQuery({
    queryKey: ["embedded_dashboards", project ?? "all"],
    queryFn: async () => {
      const all = await fetchTable<EmbeddedDashboard>("embedded_dashboards", mockEmbeds, {
        col: "display_order",
        ascending: true,
      });
      return project ? all.filter((e) => e.project === project || e.project === null) : all;
    },
  });
}

export async function createTask(input: {
  project: string;
  title: string;
  description?: string | null;
  status?: ProjectTask["status"];
  kind?: ProjectTask["kind"];
  priority?: number;
  source?: ProjectTask["source"];
}): Promise<ProjectTask> {
  const row: ProjectTask = {
    id: crypto.randomUUID(),
    project: input.project,
    title: input.title,
    description: input.description ?? null,
    status: input.status ?? "todo",
    kind: input.kind ?? "feature",
    priority: input.priority ?? 0,
    agent_owner: null,
    source: input.source ?? "laura",
    parent_task_id: null,
    created_at: new Date().toISOString(),
    completed_at: null,
  };
  if (hasSupabase && supabase) {
    try {
      const { data, error } = await supabase
        .from("project_tasks")
        .insert({
          project: row.project,
          title: row.title,
          description: row.description,
          status: row.status,
          kind: row.kind,
          priority: row.priority,
          source: row.source,
        })
        .select()
        .single();
      if (!error && data) return data as ProjectTask;
    } catch (err) {
      console.warn("[meanie] createTask supabase failed, mock-only", err);
    }
  }
  mockTasks.unshift(row);
  return row;
}

export async function updateTaskStatus(id: string, status: ProjectTask["status"]) {
  if (hasSupabase && supabase) {
    try {
      await supabase
        .from("project_tasks")
        .update({ status, completed_at: status === "done" ? new Date().toISOString() : null })
        .eq("id", id);
      return;
    } catch (err) {
      console.warn("[meanie] updateTaskStatus failed", err);
    }
  }
  const t = mockTasks.find((t) => t.id === id);
  if (t) {
    t.status = status;
    t.completed_at = status === "done" ? new Date().toISOString() : null;
  }
}

export async function createNote(input: { project: string; body: string }) {
  const row: ProjectNote = {
    id: crypto.randomUUID(),
    project: input.project,
    body: input.body,
    author: "laura",
    interpreted_by_agent_at: null,
    agent_actions: null,
    created_at: new Date().toISOString(),
  };
  if (hasSupabase && supabase) {
    try {
      const { data, error } = await supabase
        .from("project_notes")
        .insert({ project: row.project, body: row.body, author: row.author })
        .select()
        .single();
      if (!error && data) return data as ProjectNote;
    } catch (err) {
      console.warn("[meanie] createNote failed", err);
    }
  }
  mockProjectNotes.unshift(row);
  return row;
}

// ----------------------------------------------------------------------------
// Calendar — migration 0006 (events table + calendar_feed view) and the
// priorities table written by the prioritizer agent. All three hooks fall back
// to empty arrays in mock mode (no fixtures yet — calendar is post-launch).
// ----------------------------------------------------------------------------

/**
 * Fetch the unified calendar feed (events + tasks-with-due-dates +
 * service renewals) within a window of [now - 1 day, now + rangeDays].
 */
export function useCalendarFeed(rangeDays = 30) {
  const qc = useQueryClient();
  // Re-fetch when any underlying source mutates.
  useRealtimeTable("events", () => qc.invalidateQueries({ queryKey: ["calendar_feed"] }));
  useRealtimeTable("project_tasks", () =>
    qc.invalidateQueries({ queryKey: ["calendar_feed"] }),
  );
  useRealtimeTable("services", () =>
    qc.invalidateQueries({ queryKey: ["calendar_feed"] }),
  );
  return useQuery({
    queryKey: ["calendar_feed", rangeDays],
    queryFn: async (): Promise<CalendarFeedItem[]> => {
      if (!hasSupabase || !supabase) return [];
      const lower = new Date(Date.now() - 86_400_000).toISOString();
      const upper = new Date(Date.now() + rangeDays * 86_400_000).toISOString();
      try {
        const { data, error } = await supabase
          .from("calendar_feed")
          .select("*")
          .gte("scheduled_at", lower)
          .lte("scheduled_at", upper)
          .order("scheduled_at", { ascending: true });
        if (error) {
          const code = (error as { code?: string }).code;
          if (code === "42P01" || code === "PGRST205") {
            console.info("[meanie] calendar_feed view not present yet");
            return [];
          }
          console.warn("[meanie] calendar_feed query failed", error);
          return [];
        }
        return (data ?? []) as CalendarFeedItem[];
      } catch (err) {
        console.warn("[meanie] calendar_feed threw", err);
        return [];
      }
    },
  });
}

/** Raw events table — for admin / edit views. */
export function useEvents() {
  const qc = useQueryClient();
  useRealtimeTable("events", () => qc.invalidateQueries({ queryKey: ["events"] }));
  return useQuery({
    queryKey: ["events"],
    queryFn: () => fetchTable<CalendarEvent>("events", [], { col: "scheduled_at", ascending: true }),
  });
}

/**
 * Top 10 priorities, latest generation only, sorted by rank ascending.
 * Reads everything ordered by generated_at DESC then keeps the rows whose
 * generation_id matches the most-recent row.
 */
export function usePriorities() {
  const qc = useQueryClient();
  useRealtimeTable("priorities", () => qc.invalidateQueries({ queryKey: ["priorities"] }));
  return useQuery({
    queryKey: ["priorities"],
    queryFn: async (): Promise<Priority[]> => {
      if (!hasSupabase || !supabase) return [];
      try {
        // Pull the most recent batch (50 rows is plenty — generations are <= 10).
        const { data, error } = await supabase
          .from("priorities")
          .select("*")
          .order("generated_at", { ascending: false })
          .limit(50);
        if (error) {
          const code = (error as { code?: string }).code;
          if (code === "42P01" || code === "PGRST205") return [];
          console.warn("[meanie] priorities query failed", error);
          return [];
        }
        const rows = (data ?? []) as Priority[];
        if (rows.length === 0) return [];
        const latestGen = rows[0].generation_id;
        return rows
          .filter((r) => r.generation_id === latestGen)
          .sort((a, b) => a.rank - b.rank)
          .slice(0, 10);
      } catch (err) {
        console.warn("[meanie] priorities threw", err);
        return [];
      }
    },
  });
}

/**
 * Mark a calendar item done. Routes to project_tasks or events based on the
 * source_table hint. No-op when supabase isn't wired.
 */
export async function markPriorityDone(p: Priority): Promise<void> {
  if (!hasSupabase || !supabase || !p.source_table || !p.source_id) return;
  try {
    if (p.source_table === "project_tasks") {
      await supabase
        .from("project_tasks")
        .update({ status: "done", completed_at: new Date().toISOString() })
        .eq("id", p.source_id);
    } else if (p.source_table === "events") {
      await supabase.from("events").update({ status: "done" }).eq("id", p.source_id);
    }
  } catch (err) {
    console.warn("[meanie] markPriorityDone failed", err);
  }
}

/** Enqueue a reprioritize dispatch row. */
export async function requestReprioritize(): Promise<void> {
  if (!hasSupabase || !supabase) return;
  try {
    await supabase.from("dispatch_queue").insert({
      kind: "reprioritize",
      project: null,
      payload: { requested_from: "cockpit", requested_at: new Date().toISOString() },
      status: "pending",
    });
  } catch (err) {
    console.warn("[meanie] requestReprioritize failed", err);
  }
}

/**
 * Decide an approval. Writes to Supabase when configured, otherwise updates the
 * in-memory cache so the UI still demos.
 */
export async function decideApproval(id: string, decision: "approved" | "rejected"): Promise<void> {
  if (hasSupabase && supabase) {
    const { error } = await supabase
      .from("approvals")
      .update({ status: decision, decided_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw error;
    return;
  }
  // Mock mode — mutate the fixture in place so the UI updates after refetch.
  const row = mockApprovals.find((a) => a.id === id);
  if (row) {
    row.status = decision;
    row.decided_at = new Date().toISOString();
  }
}
